<template>
  <v-container fluid class="driver-share-wrapper pa-0">

    <v-row no-gutters style="height: 100%; flex-wrap: nowrap;">

      <!-- ================= LEFT PANEL ================= -->
      <v-col cols="12" md="3"
        style="height: 100%; overflow: hidden; display: flex; flex-direction: column; background: #f4f5f7; border-right: 1px solid #e9ecef; flex-shrink: 0;">
        <!-- Search -->
        <div class="search-wrapper">
          <v-text-field v-model="searchQuery" dense hide-details clearable prepend-inner-icon="mdi-magnify"
            placeholder="Search locations here.." class="search-field" outlined />
        </div>

        <!-- ================= GROUP LIST ================= -->
        <template v-if="hasGroups">
          <div class="section-header">
            <div class="section-title">All Delivery Groups</div>
            <div class="select-all" @click="toggleSelectAll">
              {{ isAllSelected ? 'Unselect All' : 'Select All' }}
            </div>

          </div>

          <div class="group-list" v-if="filteredGroups.length">
            <restaurant-group-card v-for="group in filteredGroups" :key="group.id" :group="group"
              :selected-ids="selectedGroupIds" @select="toggleGroup" @menu="openMenu" @edit="openEditModal" />
          </div>
          <!-- ================= NO SEARCH RESULTS ================= -->
          <div v-else class="left-empty">
            <div class="left-title">No Matching Groups</div>
            <div class="left-subtitle">
              No delivery groups match "{{ searchQuery }}". Try a different search term.
            </div>
          </div>
        </template>

        <!-- ================= EMPTY STATE ================= -->
        <div v-else class="left-empty">
          <img src="@/assets/location-empty.svg" class="left-illustration" />
          <div class="left-title">No Delivery Groups Yet</div>
          <div class="left-subtitle">
            Create your first delivery group to assign drivers, manage areas, and track deliveries easily.
          </div>
        </div>
      </v-col>
      <!-- ================= RIGHT PANEL ================= -->
      <v-col cols="12" md="9" class="right-panel">

        <!-- SHOW MAP WHEN GROUPS SELECTED -->
        <restaurant-group-map-view v-if="selectedGroups.length" :groups="selectedGroups" />

        <!-- SHOW EMPTY STATE WHEN NOTHING SELECTED -->
        <div v-else class="map-empty-container">
          <img src="@/assets/map-empty.svg" class="map-illustration" />
          <div class="map-title">Nothing to Show on the Map</div>
          <div class="map-subtitle">
            Select a delivery group to see locations on the map.
          </div>
          <v-btn class="create-btn" @click="openModal">
            <v-icon left>mdi-plus</v-icon>
            Create Delivery Group
          </v-btn>
        </div>

      </v-col>


    </v-row>

    <!-- MODAL -->
    <new-restaurant-group-modal v-if="showModal" :value="showModal" @input="showModal = $event" />
    <edit-restaurant-group-modal v-if="showEditModal" :group="editGroup" @close="showEditModal = false" />

  </v-container>
</template>

<script>
export default {
  data() {
    return {
      showModal: false,
      showEditModal: false,
      editGroup: null,
      searchQuery: '',
      selectedGroupId: null,
      selectedGroupIds: [],
    }
  },

  computed: {
    restaurantGroups() {
      return this.$store.getters.restaurantGroups
    },
    hasGroups() {
      return this.restaurantGroups && this.restaurantGroups.length > 0
    },
    // Filters delivery groups by name based on the search box
    filteredGroups() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        return this.restaurantGroups
      }
      const q = this.searchQuery.trim().toLowerCase()
      return this.restaurantGroups.filter(g =>
        (g.name || '').toLowerCase().includes(q)
      )
    },
    isAllSelected() {
      return this.filteredGroups.length > 0 &&
        this.filteredGroups.every(g => this.selectedGroupIds.includes(g.id))
    },
    selectedGroups() {
      return this.restaurantGroups.filter(g =>
        this.selectedGroupIds.includes(g.id)
      )
    }
  },

  mounted() {
    this.$store.dispatch('fetchRestaurantGroups')
  },

  methods: {
    openModal() { this.showModal = true },
    toggleGroup(group) {
      const exists = this.selectedGroupIds.includes(group.id)

      if (exists) {
        this.selectedGroupIds = this.selectedGroupIds.filter(id => id !== group.id)
      } else {
        this.selectedGroupIds.push(group.id)
      }
    },
    openMenu(group) { console.log('Menu clicked:', group) },
    openEditModal(group) {
      this.editGroup = group
      this.showEditModal = true
    },
    // Select/unselect only applies to the currently filtered (visible) groups
    toggleSelectAll() {
      const filteredIds = this.filteredGroups.map(g => g.id)
      if (this.isAllSelected) {
        this.selectedGroupIds = this.selectedGroupIds.filter(id => !filteredIds.includes(id))
      } else {
        this.selectedGroupIds = [...new Set([...this.selectedGroupIds, ...filteredIds])]
      }
    }

  }
}
</script>

<style scoped>
/* ================= MAIN WRAPPER ================= */
.driver-share-wrapper {
  height: calc(100vh - 64px);
  background: #fafafa;
  overflow: hidden;
}

/* ================= SEARCH ================= */
.search-wrapper {
  flex-shrink: 0;
  padding: 20px 20px 16px 20px;
}

.search-field>>>.v-input__slot {
  background: #ffffff !important;
  border-radius: 40px !important;
  min-height: 42px !important;
}

.search-field>>>.v-input__control>.v-input__slot {
  border-radius: 40px !important;
}

.search-field>>>fieldset {
  border-radius: 40px !important;
  border-color: #e9ecef !important;
}

/* ================= SECTION HEADER ================= */
.section-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px 16px 20px;
}

.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #0b1b3f;
}

.select-all {
  font-size: 14px;
  font-weight: 600;
  color: #11A8C9;
  cursor: pointer;
  text-decoration: underline;
}

/* ================= SCROLLABLE GROUP LIST ================= */
.group-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 14px 20px 20px;
}

.group-list::-webkit-scrollbar {
  width: 6px;
}

.group-list::-webkit-scrollbar-thumb {
  background: #d3d8df;
  border-radius: 6px;
}

/* ================= EMPTY STATE ================= */
.left-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 20px;
}

.left-illustration {
  width: 110px;
  margin-bottom: 20px;
}

.left-title {
  font-size: 16px;
  font-weight: 600;
  color: #0b1b3f;
  margin-bottom: 6px;
}

.left-subtitle {
  font-size: 14px;
  color: #7b8794;
  line-height: 1.6;
  max-width: 260px;
}

/* ================= RIGHT PANEL ================= */
.right-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px;
  background: #fafafa;
  height: 100%;
}

.map-wrapper {
  width: 100%;
  height: 100%;
}

.map-empty-container {
  text-align: center;
  max-width: 720px;
  width: 100%;
}

.map-illustration {
  width: 100%;
  max-width: 480px;
  margin-bottom: 36px;
}

.map-title {
  font-size: 30px;
  font-weight: 700;
  color: #0b1b3f;
  margin-bottom: 14px;
}

.map-subtitle {
  font-size: 16px;
  color: #7b8794;
  margin-bottom: 32px;
}

.create-btn {
  background-color: #11A8C9 !important;
  color: white !important;
  border-radius: 40px;
  padding: 12px 32px;
  font-weight: 500;
  text-transform: none;
  box-shadow: 0 6px 18px rgba(17, 168, 201, 0.25);
}

/* ================= MOBILE ================= */
@media (max-width: 960px) {
  .driver-share-wrapper {
    height: auto;
    overflow: visible;
  }

  .right-panel {
    padding: 60px 20px;
    height: auto;
  }

  .map-illustration {
    max-width: 280px;
  }

  .map-title {
    font-size: 22px;
  }

  .map-subtitle {
    font-size: 14px;
  }

  .create-btn {
    width: 100%;
  }
}
</style>